import { applyPageMetadata, getPageMetadata } from "@/lib/page-metadata";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute, notFound, redirect, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, CTAButton, FinalCTA, Breadcrumbs, BOOKING_URL } from "@/components/site/Layout";
import { getPracticeArea, practiceAreas, type PracticeArea } from "@/data/practice-areas";
import { CaseStudy, practiceCaseStudy } from "@/components/site/CaseStudy";
import {
  TrustBar,
  WhyDifferent,
  ProcessTimeline,
  Testimonials,
  SeoVsAds,
  InvestmentSection,
  WhoThisIsFor,
  OtherPracticeAreas,
  expandFaqs,
  InlineCTA,
} from "@/components/site/PracticeAreaSections";

export const Route = createFileRoute("/practice-areas/$slug")({
  loader: ({ params }) => {
    const raw = params.slug;
    // If the URL is the legacy form (no -lawyer-seo suffix) but matches a
    // real practice-area base slug, 301 to the new keyword-rich URL.
    if (!raw.endsWith("-lawyer-seo")) {
      const suffixed = `${raw}-lawyer-seo`;
      if (getPracticeArea(suffixed)) {
        throw redirect({ to: "/practice-areas/$slug", params: { slug: suffixed }, statusCode: 301 });
      }
    }
    const area = getPracticeArea(raw);
    if (!area) throw notFound();
    return { area };
  },
  head: ({ params, loaderData }) => {
    const a = loaderData?.area;
    if (!a) return { meta: [{ title: "Practice Area" }] };
    const faqs = expandFaqs(a);
    return applyPageMetadata(`/practice-areas/${params.slug}`, {
      meta: [
        { title: `${a.name} | SEO Services for ${a.shortName} Law Firms` },
        { name: "description", content: getPageMetadata(`/practice-areas/${params.slug}`)?.description ?? "" },
        { property: "og:title", content: `${a.name} | PageOne Lawyers` },
        { property: "og:description", content: a.headline },
        { property: "og:url", content: `https://pageonelawyers.com/practice-areas/${params.slug}` },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: `https://pageonelawyers.com/practice-areas/${params.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: a.name,
            description: a.intro,
            provider: { "@type": "Organization", name: "PageOne Lawyers" },
            serviceType: a.name,
            areaServed: "United States, United Kingdom, Canada, Australia",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    });
  },
  component: PracticeAreaPage,
  notFoundComponent: () => (
    <Layout>
      <Section><h1 className="text-3xl font-semibold">Practice area not found</h1></Section>
    </Layout>
  ),
  errorComponent: () => (
    <Layout><Section><h1 className="text-3xl">Something went wrong</h1></Section></Layout>
  ),
});

function PracticeAreaPage() {
  const { area: a } = Route.useLoaderData() as { area: PracticeArea };
  const short = a.shortName;
  const faqs = expandFaqs(a);
  const clientKeywords = [
    `best ${short.toLowerCase()} attorneys`,
    `${short.toLowerCase()} attorneys near me`,
    `${short.toLowerCase()} lawyer near me`,
    `top ${short.toLowerCase()} law firms`,
    `${short.toLowerCase()} attorneys in [your city]`,
    `${short.toLowerCase()} lawyer [your city]`,
    `affordable ${short.toLowerCase()} lawyers`,
    `experienced ${short.toLowerCase()} attorneys`,
    `${short.toLowerCase()} attorney free consultation`,
    `${short.toLowerCase()} law firm [your city]`,
  ];
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Practice Areas", href: "/practice-areas" }, { label: a.shortName }]} />
          <Eyebrow><span className="text-[var(--gold)]">Practice Area</span></Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-semibold leading-tight max-w-3xl">{a.name}</h1><LastUpdated />
          <p className="mt-6 text-xl text-primary-foreground/85 max-w-3xl">{a.headline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton to="/law-firm-seo-audit" variant="gold">Get a free {a.shortName} SEO audit</CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">Talk to a strategist</CTAButton>
          </div>
        </div>
      </section>

      {/* 2. Trust bar */}
      <TrustBar shortName={a.shortName} />

      {/* 3. Problem agitation + 4. Keywords */}
      <Section>
        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2 prose max-w-none">
            <p className="text-lg text-foreground leading-relaxed">{a.intro}</p>
            <h2 className="mt-12 text-3xl font-semibold">The problem most {a.shortName.toLowerCase()} firms face</h2>
            <ul className="mt-4 space-y-3">
              {a.painPoints.map((p) => (
                <li key={p} className="flex gap-3 list-none"><span className="text-destructive flex-shrink-0">✗</span><span className="text-muted-foreground">{p}</span></li>
              ))}
            </ul>
          </div>
          <aside className="rounded-xl border border-border bg-secondary/50 p-6 h-fit md:sticky md:top-24">
            <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">Keywords your {short.toLowerCase()} firm can rank for</div>
            <p className="mt-2 text-xs text-muted-foreground">The high-intent, client-facing searches we'll target for your practice.</p>
            <ul className="mt-4 space-y-2 text-sm">
              {clientKeywords.map((k) => (
                <li key={k} className="flex items-start gap-2"><span className="text-[var(--gold-deep)]">›</span><span>{k}</span></li>
              ))}
            </ul>
          </aside>
        </div>
      </Section>

      {/* 5. Why this niche is different */}
      <WhyDifferent shortName={a.shortName} />

      <InlineCTA
        headline={`See where your ${a.shortName.toLowerCase()} firm is losing cases.`}
        sub="Free audit covers technical, on-page, GBP, and competitor gaps, delivered in 10 business days."
        primaryLabel={`Get my free ${a.shortName} audit`}
      />

      {/* 6. What we deliver */}
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <Eyebrow>What we deliver</Eyebrow>
          <h2 className="mt-4 text-4xl font-semibold max-w-3xl">A complete {a.shortName} SEO program, built around your firm's economics.</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {a.deliverables.map((d) => (
              <div key={d} className="flex gap-4 rounded-lg bg-background border border-border p-5">
                <span className="text-[var(--gold-deep)] font-bold flex-shrink-0">✓</span>
                <p className="text-foreground">{d}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* 7. Process timeline */}
      <ProcessTimeline shortName={a.shortName} />

      {/* 8. Real outcomes */}
      <Section>
        <Eyebrow>Real outcomes</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold max-w-3xl">What {a.shortName.toLowerCase()} firms see with PageOne.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {a.results.map((r) => (
            <div key={r.label} className="rounded-xl border border-border bg-card p-8 text-center">
              <div className="font-display text-5xl font-semibold text-[var(--gold-deep)]">{r.metric}</div>
              <div className="mt-3 text-muted-foreground">{r.label}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground text-center">Results based on aggregate client data. Individual outcomes vary by market, competition, and engagement.</p>
      </Section>

      {/* 9. Case study */}
      <CaseStudy {...practiceCaseStudy(a.slug, a.name, a.shortName, a.results)} />

      {/* 10. Testimonials */}
      <Testimonials shortName={a.shortName} />

      {/* 11. SEO vs Ads */}
      <SeoVsAds shortName={a.shortName} />

      <InlineCTA
        headline="Ready to stop renting traffic and start owning rankings?"
        sub="Book a 20-minute strategy call. We'll walk your market, competitors, and the fastest path to signed cases."
        primaryLabel="Book a strategy call"
        primaryTo={BOOKING_URL}
        secondaryLabel="See pricing"
        secondaryTo="/pricing"
        tone="dark"
      />

      {/* 12. Investment / pricing */}
      <InvestmentSection shortName={a.shortName} />

      {/* 13. Who this is for */}
      <WhoThisIsFor shortName={a.shortName} />

      {/* 14. Expanded FAQ */}
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <Eyebrow>Frequently asked</Eyebrow>
          <h2 className="mt-4 text-4xl font-semibold max-w-3xl">Questions {a.shortName.toLowerCase()} firms ask before they sign.</h2>
          <div className="mt-10 space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-background p-6">
                <summary className="cursor-pointer font-semibold text-lg flex justify-between items-center">{f.q}<span className="text-[var(--gold-deep)] group-open:rotate-45 transition">+</span></summary>
                <p className="mt-4 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>
      </div>

      {/* 16. Other practice areas */}
      <OtherPracticeAreas currentSlug={a.slug} />

      <FinalCTA title={`Ready to rank your ${a.shortName.toLowerCase()} practice?`} sub="Get a free, no-obligation audit specific to your firm. We'll show you exactly where the case opportunities are leaking." />
    </Layout>
  );
}